import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getRandomString } from "../../../helpers/methods";
import { useUploadFilesForAgreement } from "../../../services/documentsService";
import {
    useAgreementDrafts,
    useGenerateAgreementDraftMutation,
    useGenerateAgreementDocxMutation,
} from "../../../services/draftsService";

export const AgreementDraftPreview = (props) => {
    const { agreementId } = props;
    const { t } = useTranslation();

    const { data: drafts, isLoading } = useAgreementDrafts();
    const { mutateAsync: generatePdf, isPending } = useGenerateAgreementDraftMutation();
    const { mutateAsync: generateDocx, isPending: isPendingDocx } = useGenerateAgreementDocxMutation();

    const { mutateAsync: uploadAgreementDocument } = useUploadFilesForAgreement();

    const [selectedDraft, setSelectedDraft] = useState(null);
    const previewDisabled = !selectedDraft || isPending || isPendingDocx;

    const downloadFile = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const handlePreview = async () => {
        // Download DOCX
        const docxResponse = await generateDocx({
            draftId: selectedDraft.id,
            requestBody: { agreement_id: agreementId },
        });
        const docxFileName = selectedDraft.file_name;
        const docxBlob = new Blob([docxResponse.data], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        downloadFile(docxBlob, docxFileName);

        // Generate PDF and upload
        const pdfResponse = await generatePdf({
            draftId: selectedDraft.id,
            requestBody: { agreement_id: agreementId },
        });

        const fileName = selectedDraft.file_name.replace(".docx", ".pdf");
        const pdfFile = new File([pdfResponse.data], fileName, {
            type: "application/pdf",
        });

        await uploadAgreementDocument({
            files: [
                {
                    file: pdfFile,
                    docs_name: fileName,
                    unique_index: getRandomString(),
                },
            ],
            // idk what these fields do
            commonData: {
                revision_parent: undefined,
                is_card_jpg: false,
                is_card_pdf: false,
                is_prospectus: false,
            },
            agreement_id: agreementId,
        });
    };

    if (isLoading) {
        return <CircularProgress />;
    }

    const labelId = "draft-select-label";

    return (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <FormControl fullWidth>
                <InputLabel id={labelId}>
                    {t("drafts.AgreementDraftPreview.pdfTemplate")}
                </InputLabel>
                <Select
                    labelId={labelId}
                    value={selectedDraft?.id || ""}
                    label={t("drafts.AgreementDraftPreview.pdfTemplate")}
                    onChange={(e) =>
                        setSelectedDraft(
                            drafts?.results.find((d) => d.id === e.target.value) || null,
                        )
                    }
                >
                    {drafts?.results.map((draft) => (
                        <MenuItem key={draft.id} value={draft.id}>
                            {draft.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <LoadingButton
                onClick={handlePreview}
                startIcon={<RemoveRedEyeOutlinedIcon />}
                disabled={previewDisabled}
                loading={isPending || isPendingDocx}
                sx={{
                    "& .MuiCircularProgress-root": {
                        color: "text.primary",
                    },
                }}
            >
                {t("drafts.AgreementDraftPreview.generate")}
            </LoadingButton>
        </Box>
    );
};
